import { useTranslation } from 'next-i18next'
import { type FC, useState } from 'react'
import { useTheme } from 'styled-components'

import {
  MAXIMUM_RADIUS_VALUE,
  MINIMUM_RADIUS_VALUE,
  RADIUS_SLIDER_HALF_POINT_KMS,
} from '@/constants/location'
import { SLIDER_DEBOUNCE } from '@/constants/others'
import { getRecentSearchLocationFromLocation } from '@/domain/location/getRecentSearchLocationFromLocation'
import { useDebounce } from '@/hooks/useDebounce'
import { useLocale } from '@/hooks/useLocale'
import { type GlobalLocation } from '@/lib/apollo/locationCache'
import { BodyText } from '@/ui/atoms/body-text'
import { Slider } from '@/ui/atoms/slider'

import {
  ConfirmButton,
  DistanceLabel,
  SliderGroupWrapper,
  SliderWrapper,
  StyledModalFooter,
} from './styled'
import { useLocationSearchSuggestions } from './useLocationSearchSuggestions'
import { radiusSliderValueTransformer } from './utils'

export type LocationModalFooterProps = {
  onLocationSubmit: (location: GlobalLocation, userModifiedRadius?: boolean) => void
  onLocationChange: (location: GlobalLocation) => void
  localLocation: GlobalLocation
  searchByRegionCheckboxValue: boolean
}

export const LocationModalFooter: FC<LocationModalFooterProps> = ({
  onLocationSubmit,
  onLocationChange,
  localLocation,
  searchByRegionCheckboxValue,
}) => {
  const [userModifiedRadius, setUserModifiedRadius] = useState<boolean>(false)
  const { t } = useTranslation('common')
  const theme = useTheme()
  const { setRecentSearchLocation } = useLocationSearchSuggestions()
  const debounce = useDebounce(SLIDER_DEBOUNCE)
  const { apiLocale } = useLocale()

  const shouldHideFooter = !searchByRegionCheckboxValue && localLocation?.isRegion
  const shouldShowRadialSlider = !localLocation?.isRegion && !searchByRegionCheckboxValue
  const radius = localLocation?.radius || RADIUS_SLIDER_HALF_POINT_KMS

  const linearRadius = radiusSliderValueTransformer(radius, true)
  const RADIUS_SLIDER_ARGS = {
    min: MINIMUM_RADIUS_VALUE,
    max: MAXIMUM_RADIUS_VALUE,
    defaultValue: linearRadius,
    marks: { 500: RADIUS_SLIDER_HALF_POINT_KMS },
  }

  const handleSubmitLocation = (location: GlobalLocation, userModifiedRadius?: boolean) => {
    const recentLocation = getRecentSearchLocationFromLocation(
      localLocation,
      searchByRegionCheckboxValue,
      apiLocale
    )
    if (recentLocation) {
      setRecentSearchLocation(recentLocation)
    }
    onLocationSubmit(location, userModifiedRadius)
  }

  const handleRadiusChange = (value: number | number[]) => {
    const radiusValue = Array.isArray(value) ? value[0] : value

    if (!userModifiedRadius) {
      setUserModifiedRadius(true)
    }

    const radius = radiusSliderValueTransformer(radiusValue)
    debounce(() => onLocationChange({ ...localLocation, radius }))()
  }

  if (shouldHideFooter) return null

  return (
    <StyledModalFooter>
      <SliderGroupWrapper>
        {shouldShowRadialSlider && (
          <>
            <DistanceLabel color={theme.colors.purple.primary}>
              {t('modals.search_location.labels.distance')}
            </DistanceLabel>

            <SliderWrapper data-testid="location-footer-radius">
              <Slider {...RADIUS_SLIDER_ARGS} value={linearRadius} onChange={handleRadiusChange} />
            </SliderWrapper>

            <BodyText color={theme.colors.grey.light1}>{`${radius}${t(
              'modals.search_location.labels.radius_units'
            )}`}</BodyText>
          </>
        )}
      </SliderGroupWrapper>
      <ConfirmButton
        data-testid="set-location-button"
        onClick={() => handleSubmitLocation(localLocation, userModifiedRadius)}
      >
        {t('modals.search_location.buttons.confirm')}
      </ConfirmButton>
    </StyledModalFooter>
  )
}
